import React, { Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { EIREntry, JournalNote, ViewMode } from 'types';
import { appPagePaths } from 'utils/navigation';
import { ErrorBoundary } from 'components/ErrorBoundary';

// ---------------------------------------------------------------------------
// Lazy page imports — each page is a separate Vite chunk, loaded on demand
// ---------------------------------------------------------------------------

const EhrPage = React.lazy(() =>
  import('pages/EhrPage').then((m) => ({ default: m.EhrPage }))
);
const JournalPage = React.lazy(() =>
  import('pages/JournalPage').then((m) => ({ default: m.JournalPage }))
);
const TutorialPage = React.lazy(() =>
  import('pages/TutorialPage').then((m) => ({ default: m.TutorialPage }))
);
const HealthDataPage = React.lazy(() =>
  import('pages/HealthDataPage').then((m) => ({ default: m.HealthDataPage }))
);
const HealthAssessmentLayout = React.lazy(() =>
  import('pages/HealthAssessmentLayout').then((m) => ({ default: m.HealthAssessmentLayout }))
);
const HealthAssessmentOverviewPage = React.lazy(() =>
  import('pages/HealthAssessmentOverviewPage').then((m) => ({ default: m.HealthAssessmentOverviewPage }))
);
const HealthAssessmentTestsPage = React.lazy(() =>
  import('pages/HealthAssessmentTestsPage').then((m) => ({ default: m.HealthAssessmentTestsPage }))
);
const HealthAssessmentAiPage = React.lazy(() =>
  import('pages/HealthAssessmentAiPage').then((m) => ({ default: m.HealthAssessmentAiPage }))
);
const HealthAssessmentResultsPage = React.lazy(() =>
  import('pages/HealthAssessmentResultsPage').then((m) => ({ default: m.HealthAssessmentResultsPage }))
);
const HealthLiteracyPage = React.lazy(() =>
  import('pages/HealthLiteracyPage').then((m) => ({ default: m.HealthLiteracyPage }))
);
const CbtProgramsPage = React.lazy(() =>
  import('pages/CbtProgramsPage').then((m) => ({ default: m.CbtProgramsPage }))
);
const HealthMapPage = React.lazy(() =>
  import('pages/HealthMapPage').then((m) => ({ default: m.HealthMapPage }))
);
const HealthVideosPage = React.lazy(() =>
  import('pages/HealthVideosPage').then((m) => ({ default: m.HealthVideosPage }))
);
const HealthActionsPage = React.lazy(() =>
  import('pages/HealthActionsPage').then((m) => ({ default: m.HealthActionsPage }))
);
const NutritionPage = React.lazy(() =>
  import('pages/NutritionPage').then((m) => ({ default: m.NutritionPage }))
);
const CareCoachPage = React.lazy(() =>
  import('pages/CareCoachPage').then((m) => ({ default: m.CareCoachPage }))
);
const SubscriptionSuccessPage = React.lazy(() =>
  import('pages/SubscriptionSuccessPage').then((m) => ({ default: m.SubscriptionSuccessPage }))
);
const SubscriptionCancelPage = React.lazy(() =>
  import('pages/SubscriptionCancelPage').then((m) => ({ default: m.SubscriptionCancelPage }))
);
const FeedbackPage = React.lazy(() =>
  import('pages/FeedbackPage').then((m) => ({ default: m.FeedbackPage }))
);
const CalendarPage = React.lazy(() =>
  import('pages/CalendarPage').then((m) => ({ default: m.CalendarPage }))
);
const GetCarePage = React.lazy(() =>
  import('pages/GetCarePage').then((m) => ({ default: m.GetCarePage }))
);
const EirDocsPage = React.lazy(() =>
  import('pages/EirDocsPage').then((m) => ({ default: m.EirDocsPage }))
);
const RoadmapPage = React.lazy(() =>
  import('pages/RoadmapPage').then((m) => ({ default: m.RoadmapPage }))
);
const PrivacyPage = React.lazy(() =>
  import('pages/PrivacyPage').then((m) => ({ default: m.PrivacyPage }))
);
const EirChatPage = React.lazy(() =>
  import('pages/EirChatPage').then((m) => ({ default: m.EirChatPage }))
);
const SkillsLibraryPage = React.lazy(() =>
  import('pages/SkillsLibraryPage').then((m) => ({ default: m.SkillsLibraryPage }))
);
const MyHealthDashboard = React.lazy(() =>
  import('pages/MyHealthDashboard').then((m) => ({ default: m.MyHealthDashboard }))
);
const HealthPillarPage = React.lazy(() =>
  import('pages/HealthPillarPage').then((m) => ({ default: m.HealthPillarPage }))
);
const TrackingDashboardPage = React.lazy(() =>
  import('pages/TrackingDashboardPage').then((m) => ({ default: m.TrackingDashboardPage }))
);

const RequestCarePage = React.lazy(() =>
  import('pages/RequestCarePage').then((m) => ({ default: m.RequestCarePage }))
);

// ---------------------------------------------------------------------------

const PageLoader = () => (
  <div className="flex flex-1 items-center justify-center h-full">
    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
  </div>
);

interface PageRoutesProps {
  hasEhrData: boolean;
  entries: EIREntry[];
  currentView: ViewMode;
  onViewChange: (view: ViewMode) => void;
  showJournalNotes: boolean;
  onToggleJournalNotes: () => void;
  displayedJournalNotes: JournalNote[];
  journalNotes: JournalNote[];
  onAddJournalNote: (input: { content: string; wellbeingScore: number }) => void;
  onDeleteJournalNote: (noteId: string) => void;
  onRunHealthAnalysis: () => void;
  onLoadFile?: () => void;
  onFetchFrom1177?: () => void;
  isFetchingFrom1177?: boolean;
}

export const PageRoutes: React.FC<PageRoutesProps> = ({
  hasEhrData,
  entries,
  currentView,
  onViewChange,
  showJournalNotes,
  onToggleJournalNotes,
  displayedJournalNotes,
  journalNotes,
  onAddJournalNote,
  onDeleteJournalNote,
  onRunHealthAnalysis,
  onLoadFile,
  onFetchFrom1177,
  isFetchingFrom1177
}) => (
  <Suspense fallback={<PageLoader />}>
    <Routes>
      <Route
        path={appPagePaths.ehr}
        element={(
          <EhrPage
            hasData={hasEhrData}
            entries={entries}
            currentView={currentView}
            onViewChange={onViewChange}
            showJournalNotes={showJournalNotes}
            onToggleJournalNotes={onToggleJournalNotes}
            displayedJournalNotes={displayedJournalNotes}
            onLoadFile={onLoadFile}
            onFetchFrom1177={onFetchFrom1177}
            isFetchingFrom1177={isFetchingFrom1177}
            onRunHealthAnalysis={onRunHealthAnalysis}
          />
        )}
      />
      <Route
        path={appPagePaths.journal}
        element={(
          <JournalPage
            notes={journalNotes}
            onAddNote={onAddJournalNote}
            onDeleteNote={onDeleteJournalNote}
          />
        )}
      />
      <Route path={appPagePaths.tutorial} element={<TutorialPage />} />
      <Route path={appPagePaths['health-data']} element={<HealthDataPage />} />
      <Route path={appPagePaths['health-assessment']} element={<HealthAssessmentLayout />}>
        <Route index element={<HealthAssessmentOverviewPage />} />
        <Route path="tests" element={<HealthAssessmentTestsPage />} />
        <Route path="ai" element={<HealthAssessmentAiPage />} />
        <Route path="results" element={<HealthAssessmentResultsPage />} />
      </Route>
      <Route path={appPagePaths['health-programs']} element={<CbtProgramsPage />} />
      <Route path={appPagePaths['health-literacy']} element={<HealthLiteracyPage />} />
      <Route path={appPagePaths['cbt-programs']} element={<CbtProgramsPage />} />
      <Route path={appPagePaths['health-map']} element={<HealthMapPage />} />
      <Route path={appPagePaths['health-videos']} element={<HealthVideosPage />} />
      <Route path={appPagePaths['health-actions']} element={<HealthActionsPage />} />
      <Route path={appPagePaths.calendar} element={<CalendarPage />} />
      <Route path={appPagePaths.nutrition} element={<NutritionPage />} />
      <Route path={appPagePaths['care-coach']} element={<CareCoachPage />} />
      <Route
        path={appPagePaths['request-care']}
        element={(
          <ErrorBoundary>
            <RequestCarePage />
          </ErrorBoundary>
        )}
      />
      <Route path={appPagePaths.feedback} element={<FeedbackPage />} />
      <Route path={appPagePaths['personal-growth']} element={<Navigate to="/my-health" replace />} />
      <Route path={appPagePaths['get-care']} element={<GetCarePage />} />
      <Route path={appPagePaths['eir-docs']} element={<EirDocsPage />} />
      <Route path={appPagePaths['eir-chat']} element={<EirChatPage />} />
      <Route path={appPagePaths.skills} element={<SkillsLibraryPage />} />
      <Route path={appPagePaths.roadmap} element={<RoadmapPage />} />
      <Route path={appPagePaths['for-you']} element={<Navigate to="/my-health" replace />} />
      <Route path="/for-you/*" element={<Navigate to="/my-health" replace />} />
      <Route path={appPagePaths.privacy} element={<PrivacyPage />} />
      <Route path="/my-health" element={<MyHealthDashboard onAddNote={onAddJournalNote} />} />
      <Route path="/my-health/:pillar" element={<HealthPillarPage />} />
      <Route path="/tracking" element={<TrackingDashboardPage />} />
      <Route path="/health-records" element={(
        <EhrPage
          hasData={hasEhrData}
          entries={entries}
          currentView={currentView}
          onViewChange={onViewChange}
          showJournalNotes={showJournalNotes}
          onToggleJournalNotes={onToggleJournalNotes}
          displayedJournalNotes={displayedJournalNotes}
          onLoadFile={onLoadFile}
          onFetchFrom1177={onFetchFrom1177}
          isFetchingFrom1177={isFetchingFrom1177}
          onRunHealthAnalysis={onRunHealthAnalysis}
        />
      )} />
      <Route path="/subscription/success" element={<SubscriptionSuccessPage />} />
      <Route path="/subscription/cancel" element={<SubscriptionCancelPage />} />
      <Route path="*" element={<Navigate to={appPagePaths['eir-chat']} replace />} />
    </Routes>
  </Suspense>
);
